import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface SectionRow {
  id: string
  showId: string
  name: string
  capacity: number
  sold: number
  held: number
  pricePence: number
  tierRestriction: number | null
}

export function TicketingPage() {
  const scope = usePerformanceScope()
  const { data, error, isLoading } = useQuery<SectionRow[], ApiClientError>({
    queryKey: ['ticketing', 'sections', ...scope.queryKeySuffix],
    queryFn: () => artistFetch<SectionRow[]>(`/ticketing/sections${scope.toQueryString()}`),
  })

  const rows = scope.filterByShowIds(data ?? [], (r) => r.showId)

  return (
    <div>
      <PageHeader
        title="Ticketing"
        subtitle="Direct ticket sales — tier cascade, holds, and live availability."
      />
      <PerformanceScopeBanner />
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          emptyMessage="No ticket sections yet. Open a show from the Shows page to add its first section."
          columns={[
            { key: 'name', header: 'Section', render: (r) => r.name },
            {
              key: 'capacity',
              header: 'Capacity',
              align: 'right',
              render: (r) => r.capacity.toLocaleString(),
            },
            {
              key: 'sold',
              header: 'Sold',
              align: 'right',
              render: (r) => r.sold.toLocaleString(),
            },
            {
              key: 'held',
              header: 'Held',
              align: 'right',
              render: (r) => r.held.toLocaleString(),
            },
            {
              key: 'price',
              header: 'Price',
              align: 'right',
              render: (r) => `£${(r.pricePence / 100).toFixed(2)}`,
            },
            {
              key: 'tier',
              header: 'Tier',
              render: (r) => (r.tierRestriction ? `≥ Tier ${r.tierRestriction}` : 'All'),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}
